import type { StoryBibleGeneratedEntity, StoryBibleSectionKey } from "~shared/agenticChat/storyBibleGeneratedEntity";
import { isStoryBibleCtaType } from "~shared/agenticChat/storyBibleCtaTypes";
import type { CTAAction, CTAActionType } from "./ThinkingStep/cta-types";

export { isStoryBibleCtaType };

export type UserQuestionPayload = {
  questions?: Array<{
    question?: string;
    inputType?: string;
    options?: Array<{ value?: string; label?: string }>;
  }>;
};

export const STORY_BIBLE_SECTION_ICON_PATHS: Record<StoryBibleSectionKey, string> = {
  seed: "/assets/story-bible/seed-icon.svg",
  genre: "/assets/story-bible/genre-icon.svg",
  style: "/assets/story-bible/style-icon.svg",
  synopsis: "/assets/story-bible/synopsis-icon.svg",
  characters: "/assets/story-bible/characters-icon.svg",
  worldbuilding: "/assets/story-bible/worldbuilding-icon.svg",
  outline: "/assets/story-bible/outline-icon.svg",
};

const DEFAULT_STORY_BIBLE_SECTION_ICON = STORY_BIBLE_SECTION_ICON_PATHS.synopsis;

/** Icon for the Story Bible section targeted by an agentic CTA. */
export function getStoryBibleSectionIconForCta(ctaType: CTAActionType): string {
  switch (ctaType) {
    case "generate_chapter":
    case "rewrite_chapter":
    case "generate_outline":
    case "rewrite_outline":
      return STORY_BIBLE_SECTION_ICON_PATHS.outline;
    case "generate_character":
    case "generate_cast":
    case "generate_trait":
    case "rewrite_trait":
      return STORY_BIBLE_SECTION_ICON_PATHS.characters;
    case "generate_worldbuilding":
    case "generate_worldbuilding_trait":
    case "rewrite_worldbuilding_trait":
      return STORY_BIBLE_SECTION_ICON_PATHS.worldbuilding;
    case "generate_synopsis":
    case "rewrite_synopsis":
      return STORY_BIBLE_SECTION_ICON_PATHS.synopsis;
    default: {
      const _exhaustive: never = ctaType;
      return _exhaustive;
    }
  }
}

/** Icon for a generated Story Bible entity result card. */
export function getStoryBibleSectionIconForGeneratedEntity(entity: StoryBibleGeneratedEntity): string {
  if (entity.entityType === "character") {
    return STORY_BIBLE_SECTION_ICON_PATHS.characters;
  }
  if (entity.entityType === "worldbuilding") {
    return STORY_BIBLE_SECTION_ICON_PATHS.worldbuilding;
  }
  if (entity.entityType === "outline_chapter") {
    return STORY_BIBLE_SECTION_ICON_PATHS.outline;
  }
  if (entity.entityType === "story_bible_section") {
    return STORY_BIBLE_SECTION_ICON_PATHS[entity.entityId as StoryBibleSectionKey]
      ?? DEFAULT_STORY_BIBLE_SECTION_ICON;
  }
  return DEFAULT_STORY_BIBLE_SECTION_ICON;
}

export function isGenerateCastCharacterCountAssistantMetadata(metadata: unknown): boolean {
  if (!metadata || typeof metadata !== "object") return false;
  return (metadata as { generateCastCharacterCountPrompt?: unknown }).generateCastCharacterCountPrompt === true;
}

export function isAnsweringGenerateCastCharacterCountQuestion(
  answeredAssistantMessage: { role?: string; metadata?: unknown } | null | undefined,
): boolean {
  return answeredAssistantMessage?.role === "assistant"
    && isGenerateCastCharacterCountAssistantMetadata(answeredAssistantMessage.metadata);
}

type CastCountUserQuestion = UserQuestionPayload;

/** True when the ask_user_question payload is the generate-cast size picker. */
export function userQuestionIsGenerateCastCharacterCount(
  userQuestion: CastCountUserQuestion | null | undefined,
): boolean {
  const questions = userQuestion?.questions;
  if (!questions?.length) return false;
  return questions.some(
    (q) => Array.isArray(q.options) && q.options.some((opt) => opt?.value === "cast_count_default"),
  );
}

function submittedPairsMatchUserQuestion(
  pairs: Array<{ question: string }>,
  userQuestion: CastCountUserQuestion | null | undefined,
): boolean {
  const submitted = new Set(pairs.map((p) => p.question.trim()));
  return (userQuestion?.questions ?? []).some(
    (q) => typeof q.question === "string" && submitted.has(q.question.trim()),
  );
}

/** True while generate_cast is actively running — not while awaiting cast-size input. */
export function isGenerateCastCtaBlocking(cta: CTAAction | null | undefined): boolean {
  return cta?.type === "generate_cast" && cta.status === "in_progress";
}

/** True while a Story Bible CTA session is open (instructions or run in flight). */
export function isStoryBibleCtaBlockingRetrigger(
  cta: Pick<CTAAction, "type" | "status"> | null | undefined,
): boolean {
  if (!cta || !isStoryBibleCtaType(cta.type)) return false;
  return cta.status === "in_progress" || cta.status === "awaiting_input";
}

export function getUserQuestionSignature(
  questions: UserQuestionPayload["questions"] | null | undefined,
): string {
  if (!Array.isArray(questions) || questions.length === 0) return "";
  return questions
    .map((q) => `${String(q.question ?? "")}::${String(q.inputType ?? "")}`)
    .join("||");
}

/** Resolve the assistant message that asked the question the user is answering. */
export function findAssistantMessageForUserQuestion(
  messages: Array<{ id?: string; role?: string; metadata?: unknown }> | null | undefined,
  userQuestion: UserQuestionPayload | null | undefined,
): { role?: string; metadata?: unknown } | undefined {
  if (!userQuestion?.questions?.length) return undefined;
  const signature = getUserQuestionSignature(userQuestion.questions);
  if (!signature) return undefined;
  return [...(messages ?? [])]
    .reverse()
    .find((message) => {
      if (message.role !== "assistant") return false;
      const asked = (message.metadata as { userQuestion?: UserQuestionPayload } | undefined)?.userQuestion;
      return getUserQuestionSignature(asked?.questions) === signature;
    });
}

/**
 * Whether answering this question should show generate_cast floating progress.
 * Uses the open question (savedQuestion) and submitted pairs; when the assistant row is
 * already cached, also requires it to be the cast-size prompt so we do not mis-fire on a
 * different questionAsked row.
 */
export function shouldStartGenerateCastProgressOnQuestionAnswer(
  answeredAssistantMessage: { role?: string; metadata?: unknown } | null | undefined,
  savedQuestion: CastCountUserQuestion | null | undefined,
  pairs: Array<{ question: string }>,
): boolean {
  if (!userQuestionIsGenerateCastCharacterCount(savedQuestion)) return false;
  if (!submittedPairsMatchUserQuestion(pairs, savedQuestion)) return false;

  if (isAnsweringGenerateCastCharacterCountQuestion(answeredAssistantMessage)) {
    const assistantQuestion = (answeredAssistantMessage?.metadata as { userQuestion?: CastCountUserQuestion } | undefined)
      ?.userQuestion;
    return (
      userQuestionIsGenerateCastCharacterCount(assistantQuestion)
      && submittedPairsMatchUserQuestion(pairs, assistantQuestion)
    );
  }

  // Assistant row may not be in the message cache yet; savedQuestion is what the user answered.
  if (answeredAssistantMessage?.role === "assistant") {
    const assistantQuestion = (answeredAssistantMessage.metadata as { userQuestion?: CastCountUserQuestion } | undefined)
      ?.userQuestion;
    if (assistantQuestion && !userQuestionIsGenerateCastCharacterCount(assistantQuestion)) {
      return false;
    }
  }

  return true;
}

export function isStoryBibleTraitCta(ctaType: CTAActionType): boolean {
  return ctaType === "generate_trait"
    || ctaType === "rewrite_trait"
    || ctaType === "generate_worldbuilding_trait"
    || ctaType === "rewrite_worldbuilding_trait";
}

/** Use the action label, never user-provided content, for the progress title. */
export function getStoryBibleCtaProgressTitle(ctaAction: CTAAction): string {
  return ctaAction.displayTitle.trim() || "Story Bible";
}

/** Primary and optional trait subtitle for the floating Story Bible CTA progress card. */
export function getStoryBibleCtaProgressHeadline(ctaAction: CTAAction): {
  primary: string;
  secondary?: string;
} {
  if (isStoryBibleTraitCta(ctaAction.type)) {
    const primary = getStoryBibleCtaProgressTitle(ctaAction);
    const secondary = ctaAction.traitLabel?.trim();
    if (secondary && secondary !== primary) {
      return { primary, secondary };
    }
    return { primary };
  }
  return { primary: getStoryBibleCtaProgressTitle(ctaAction) };
}
